import { useEffect, useMemo, useRef, useState } from "react";

import { AgGridReact } from "ag-grid-react";
import Box from "@mui/material/Box";
import MuiDialog from "@mui/material/Dialog";
import DialogTitle from "@mui/material/DialogTitle";
import DialogContent from "@mui/material/DialogContent";

import { withTranslation } from "react-i18next";
import { isEmpty } from "lodash";

import fpCommonService from "@plannel/services/fp/fp-common-service";
import FpGridSetting from "@plannel/pages/factory-plan/common/grid";
import dateUtils from "@plannel/utils/date-util";
import debug from "@plannel/utils/debug-util";

/**
 * 인벤토리 조회/선택 전용 팝업 — old `InventoryPopup.jsx`(NOBUTTONS, 더블클릭 선택)와 동일하게
 * 선택 전용(관리 기능 없음). 인벤토리는 품목/공장/스테이지와 조인된 목록으로 표시되고,
 * 더블클릭 시 선택 행의 인벤토리+품목 5개 필드를 onSelect로 돌려준 뒤 닫힌다(StagePopup과 동일 구성).
 *
 * props: open, onClose(), planScope, onSelect({inventoryCd, inventoryNm, itemCd, itemNm, itemClassCd})
 */
const InventoryPopup = ({ t, open, onClose, planScope, onSelect }) => {
  const gridRef = useRef();
  const [rowData, setRowData] = useState([]);
  const gridOptions = useMemo(() => FpGridSetting({ title: t("FP_INVENTORY_SELECT"), viewName: "" }), [t]);

  const columnDefs = useMemo(() => ([
    { headerName: t("FP_INVENTORY_CD"), field: "inventoryCd", colId: "inventoryCd", width: 160, editable: false },
    { headerName: t("FP_INVENTORY_NAME"), field: "inventoryNm", colId: "inventoryNm", width: 200, editable: false },
    { headerName: t("FP_ITEM_CD"), field: "itemCd", colId: "itemCd", width: 160, editable: false },
    { headerName: t("FP_ITEM_NM"), field: "itemNm", colId: "itemNm", width: 200, editable: false },
    { headerName: t("FP_ITEM_CLASS_CD"), field: "itemClassCd", colId: "itemClassCd", width: 110, editable: false, type: ["centerAligned"],
      valueFormatter: (params) => {
        const key = `FP_ITEM_CLASS_CD_${params.value}`;
        const label = t(key);
        return label !== key ? label : params.value;
      } },
    { headerName: t("FP_PLANT_CD"), field: "plantCd", colId: "plantCd", width: 160, editable: false },
    { headerName: t("FP_PLANT_NM"), field: "plantNm", colId: "plantNm", width: 200, editable: false },
    { headerName: t("FP_STAGE_CD"), field: "stageCd", colId: "stageCd", width: 160, editable: false },
    { headerName: t("FP_STAGE_NM"), field: "stageNm", colId: "stageNm", width: 200, editable: false },
    {
      headerName: t("FP_COL_AUDIT"), groupId: "audit", marryChildren: true, openByDefault: false,
      children: [
        { headerName: t("updatedTs"), field: "modifyDttm", colId: "modifyDttm", type: ["nonEditableColumn", "centerAligned"], width: 150, valueFormatter: dateUtils.formatDateTime },
        { headerName: t("updatedBy"), field: "modifyUser", colId: "modifyUser", type: ["nonEditableColumn", "centerAligned"], width: 110, columnGroupShow: "open" },
        { headerName: t("createdTs"), field: "createDttm", colId: "createDttm", type: ["nonEditableColumn", "centerAligned"], width: 150, valueFormatter: dateUtils.formatDateTime, columnGroupShow: "open" },
        { headerName: t("createdBy"), field: "createUser", colId: "createUser", type: ["nonEditableColumn", "centerAligned"], width: 110, columnGroupShow: "open" }
      ]
    }
  ]), [t]);

  const loadInventories = () => {
    if (!planScope) return;
    fpCommonService.getInventories({ planScope })
      .then(response => setRowData(isEmpty(response.data) ? [] : response.data))
      .catch(e => { debug(e); setRowData([]); });
  };

  useEffect(() => {
    if (!open || !planScope) return;
    loadInventories();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open, planScope]);

  // old NOBUTTONS와 동일하게 더블클릭으로만 확정 — 인벤토리 선택 시 품목 정보까지 함께 write-back.
  const onRowDoubleClicked = (e) => {
    if (!e.data) return;
    onSelect({
      inventoryCd: e.data.inventoryCd,
      inventoryNm: e.data.inventoryNm,
      itemCd: e.data.itemCd,
      itemNm: e.data.itemNm,
      itemClassCd: e.data.itemClassCd
    });
    onClose();
  };

  return (
    <MuiDialog open={open} onClose={onClose} maxWidth="xl" fullWidth>
      <DialogTitle>{t("FP_INVENTORY_SELECT")}</DialogTitle>
      <DialogContent>
        <Box className="ag-theme-balham" sx={{ height: 440, width: "100%" }}>
          <AgGridReact
            ref={gridRef}
            rowData={rowData}
            columnDefs={columnDefs}
            onRowDoubleClicked={onRowDoubleClicked}
            {...gridOptions}
            rowSelection={{ mode: "singleRow", checkboxes: false, enableClickSelection: true }}
          />
        </Box>
      </DialogContent>
    </MuiDialog>
  );
};

export default withTranslation()(InventoryPopup);
